import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { debounce, fetchConfig } from '@theme/utilities';

/**
 * Keeps a "gift" cart line (Ancarta Classic Socks — White) synced to the
 * quantity the Brand Rep WELCOME20 welcome offer entitles her to, whenever
 * WELCOME20 is actually applied and applicable to her cart.
 *
 * AUTHORITY BOUNDARY: this module NEVER decides what becomes free — that is
 * exclusively the job of the WELCOME20 Discount Function, which is the only
 * thing that ever runs at checkout/order time. This module's only job is
 * making sure the right NUMBER of White sock units is present in the cart
 * so the Function has something to discount. If this logic is ever wrong —
 * wrong quantity, wrong timing, runs when it shouldn't — the worst outcome
 * is a wrong number of $0-eligible sock units sitting in a cart; it can
 * never make an item free that the Function wouldn't have made free anyway.
 *
 * Trigger condition: cart.discount_codes contains "welcome20" with
 * applicable: true.
 *
 * CORRECTED 2026-09-11 (previous comment here was wrong): live testing on a
 * genuinely anonymous, non-authenticated cart proved WELCOME20 applies with
 * zero customer login/authentication requirement — confirmed directly
 * against the deployed welcome20-discount Function's own source, which
 * explicitly documents "Deliberately does NOT read cart.buyerIdentity, any
 * customer metafield/tag, or product/collection membership — anyone who
 * enters the code (guest, logged in, Brand Rep or not) gets the same result
 * for the same cart. Only total qualifying quantity decides eligibility and
 * tier." The only real precondition is `applicable: true` itself — Shopify
 * marks a code inapplicable if a different, non-combining code (e.g. this
 * store's private Brand Rep BOGO codes) is already active on the same cart,
 * which is what an earlier, mistaken test run had actually hit — not an
 * authentication gate. No separate Brand Rep check is needed or possible
 * from the theme (customer.metafields.brand_rep.* has no storefront
 * access) — and none is needed, since the Function itself never asks for
 * one.
 */

const WHITE_SOCK_VARIANT_ID = 46510415315121;
const GIFT_PROPERTY_KEY = '_brand_rep_welcome_gift';
const GIFT_PROPERTY_VALUE = 'true';
const WELCOME20_CODE = 'welcome20';
const ELIGIBLE_PRODUCT_IDS_ELEMENT_ID = 'brand-rep-eligible-product-ids';

// Real membership of the "Brand Rep Welcome Offer Eligible" collection
// (gid://shopify/Collection/359338836145), rendered by ancarta-header.liquid
// into a JSON data island on every page load. Reading real collection
// membership — instead of guessing eligibility from product type — means
// this can never drift out of sync with however that collection is actually
// defined today or in the future. `null` (data island missing/unparseable)
// deliberately makes every line non-qualifying rather than falling back to
// a duplicated, fragile type-exclusion guess: the only possible effect is
// under-counting cart quantity, never a wrong price, since the Function is
// still the sole pricing authority.
function loadEligibleProductIds() {
  try {
    const el = document.getElementById(ELIGIBLE_PRODUCT_IDS_ELEMENT_ID);
    if (!el || !el.textContent) return null;
    const ids = JSON.parse(el.textContent);
    if (!Array.isArray(ids)) return null;
    return new Set(ids.map((id) => Number(id)));
  } catch (error) {
    console.error('[brand-rep-welcome-gift] failed to read eligible product ids', error);
    return null;
  }
}

const eligibleProductIds = loadEligibleProductIds();

let dispatchingOwnUpdate = false;
let inFlightSync = null;
let syncQueued = false;

function isGiftLine(item) {
  return !!(item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE);
}

function isQualifyingClothingLine(item) {
  if (isGiftLine(item)) return false;
  if (!eligibleProductIds) return false;
  return eligibleProductIds.has(item.product_id);
}

function isWelcome20Applicable(cart) {
  const codes = cart.discount_codes || [];
  return codes.some((entry) => entry.code && entry.code.toLowerCase() === WELCOME20_CODE && entry.applicable);
}

/** 0-1 qualifying pieces -> 0. Exactly 2 -> 1. 3 or more -> 3 (never a cap-sounding "max"). */
function targetGiftQuantity(qualifyingQty) {
  if (qualifyingQty >= 3) return 3;
  if (qualifyingQty === 2) return 1;
  return 0;
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

// `/cart/add.js` (unlike `/cart/change.js`) returns only `{ items, sections }`
// when a `sections` param is sent — no `currency`, no `item_count` — so
// `createCartFromAjaxResponse()` throws trying to format a price with no
// currency code. A fresh `/cart.json` fetch always has the full shape, so
// that (not the raw mutation response) is what feeds the event's `cart`.
// `result` is still the right source for `.sections`, since that's the
// actual rendered HTML from the mutation that just happened.
async function dispatchRefresh({ action, lineKey, quantity, result }) {
  const freshCart = await fetchCart();
  dispatchingOwnUpdate = true;
  try {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ id: lineKey || '', quantity }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
          detail: {
            sections: result.sections,
            items: freshCart.items,
            itemCount: freshCart.item_count,
            source: 'brand-rep-welcome-gift',
            didError: false,
          },
        }),
      })
    );
  } finally {
    dispatchingOwnUpdate = false;
  }
}

async function removeGiftLine(currentLine, sections) {
  const body = JSON.stringify({
    id: currentLine.key,
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const data = await response.json();
  if (!response.ok || data.status) return null;
  return data;
}

async function addGiftLine(quantity, sections) {
  const body = JSON.stringify({
    items: [{ id: WHITE_SOCK_VARIANT_ID, quantity, properties: { [GIFT_PROPERTY_KEY]: GIFT_PROPERTY_VALUE } }],
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
  const data = await response.json();
  if (!response.ok || data.status) return { ok: false, data };
  return { ok: true, data };
}

async function updateGiftLine(currentLine, quantity, sections) {
  const body = JSON.stringify({
    id: currentLine.key,
    quantity,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const data = await response.json();
  if (!response.ok || data.status) return { ok: false, data };
  return { ok: true, data };
}

/**
 * The actual sync work, unchanged from before this file's checkout-gate
 * refactor (2026-09-11) — split out of `runSync` purely so `ensureSynced`
 * (below) can await one genuine, freshly-started pass on demand, distinct
 * from `runSync`'s own fire-and-forget/queuing discipline used by the
 * cart-event listeners at the bottom of this file.
 */
async function performSync() {
  try {
    const cart = await fetchCart();
    const currentGiftLine = cart.items.find(isGiftLine) || null;
    const currentGiftQty = currentGiftLine ? currentGiftLine.quantity : 0;

    let targetQty = 0;
    if (isWelcome20Applicable(cart)) {
      const qualifyingQty = cart.items.filter(isQualifyingClothingLine).reduce((sum, item) => sum + item.quantity, 0);
      targetQty = targetGiftQuantity(qualifyingQty);
    }

    if (targetQty === currentGiftQty) return; // already correct — nothing to do, no request made

    const sections = cartSectionIds();

    if (targetQty === 0) {
      const result = await removeGiftLine(currentGiftLine, sections);
      if (result) await dispatchRefresh({ action: 'remove', lineKey: currentGiftLine.key, quantity: 0, result });
      return;
    }

    const outcome = currentGiftLine
      ? await updateGiftLine(currentGiftLine, targetQty, sections)
      : await addGiftLine(targetQty, sections);

    if (!outcome.ok) {
      // White socks unavailable (or any other add/update failure): never
      // retry in a loop, never substitute another colour, never block her
      // from buying her clothing. Surface it through the theme's own error
      // channel and leave the cart exactly as it was.
      document.dispatchEvent(
        new CartErrorEvent({
          error:
            (outcome.data && (outcome.data.description || outcome.data.message)) ||
            'Your free White Classic Socks are currently unavailable — you can still complete your order.',
          code: 'INVALID',
        })
      );
      return;
    }

    await dispatchRefresh({
      action: currentGiftLine ? 'update' : 'add',
      lineKey: currentGiftLine ? currentGiftLine.key : '',
      quantity: targetQty,
      result: outcome.data,
    });
  } catch (error) {
    console.error('[brand-rep-welcome-gift] sync failed', error);
  }
}

/**
 * Fire-and-forget entry point used by the cart-event listeners below: if a
 * sync is already running, just flags another pass for after it finishes
 * rather than starting a second one concurrently.
 */
function runSync() {
  if (inFlightSync) {
    syncQueued = true;
    return inFlightSync;
  }
  inFlightSync = performSync().finally(() => {
    inFlightSync = null;
    if (syncQueued) {
      syncQueued = false;
      scheduleSync();
    }
  });
  return inFlightSync;
}

/**
 * Awaited entry point for callers that need to KNOW the gift line reflects
 * current cart state before proceeding (see checkout-gift-sync-gate.js) —
 * unlike `runSync`, this always performs (and awaits) one fresh pass after
 * any already-in-flight pass settles, so it never returns based on stale
 * eligibility from before the caller's own cart change.
 *
 * Exposed via `window.AncartaGiftSync`, NOT a relative ES module `import`
 * from checkout-gift-sync-gate.js — see brand-rep-private-bogo-gift.js's
 * identical doc comment on its own `ensureSynced` for the full explanation
 * of the real, reproduced-live duplicate-line bug a relative import caused
 * (a second module instance from a URL missing this file's own Shopify
 * asset cache-busting query string).
 */
export async function ensureSynced() {
  if (inFlightSync) await inFlightSync;
  await performSync();
}

window.AncartaGiftSync = window.AncartaGiftSync || {};
window.AncartaGiftSync.ensureWelcomeGiftSynced = ensureSynced;

const scheduleSync = debounce(runSync, 400);

document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  if (dispatchingOwnUpdate) return; // ignore the event we just dispatched ourselves
  scheduleSync();
});

document.addEventListener(StandardEvents.cartDiscountUpdate, () => {
  scheduleSync();
});

// Covers arriving via the WELCOME20-preapplied login return URL, where the
// discount was attached server-side before this script ever ran.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSync());
} else {
  scheduleSync();
}
